import { inject, Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, mapTo, mergeMap, take, tap } from 'rxjs/operators';
import { CONNECTION_ERROR_CODES, isConnectionErrorCode } from '../../enums/connection-error-codes.enum';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { SYNC_REQUEST_TYPE } from '../../enums/sync-request-type.enum';
import { SynchronizationInterface } from '../../interfaces/synchronization.interface';
import { AppNotificationType } from '../app-notification/app-notification.model';
import { AppNotificationService } from '../app-notification/app-notification.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { EventService } from '../event/event.service';
import { LocalStorageAppStateService } from '../local-storage/local-storage-app-state.service';
import { SyncQueueService } from './sync-queue.service';
import { SyncRequestModel } from './sync-request.model';
import {
    DateManipulationCheckInstanceToken,
    DateManipulationCheckInterface,
} from '../../interfaces/date-manipulation-check.interface';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import { Store } from '@ngrx/store';
import { notificationRefreshRequired } from '../../state/notification/actions/notification.actions';
import { isNgRxEnabled } from '../../state/notification/notification-updates';

@Injectable({
    providedIn: 'root',
})
export class SynchronizationService implements SynchronizationInterface {
    private readonly store = inject(Store);
    private readonly ngrxEnabled = isNgRxEnabled();
    private lastSync: moment.Moment;
    private lastSyncBootStock: moment.Moment;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly connectivityService: ConnectivityService,
        private readonly syncQueueService: SyncQueueService,
        private readonly eventService: EventService,
        private readonly localStorageAppStateService: LocalStorageAppStateService,
        private readonly offlineService: OfflineService,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        @Inject(DateManipulationCheckInstanceToken)
        private readonly dateManipulationCheckService: DateManipulationCheckInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {
        this.lastSync = moment.invalid();
        eventService.encryptionInitialized$.subscribe(() => {
            this.localStorageAppStateService
                .getLastSyncBootStockTimeAsync()
                .then((lastSyncBootStock) => {
                    this.lastSyncBootStock = lastSyncBootStock;
                })
                .catch((error) => {
                    this.log.error(
                        this.constructor.name,
                        'Error getting last sync boot stock time from local storage',
                        JSON.stringify(error),
                    );
                });
        });

        this.connectivityService.isOnline$
            .pipe(
                filter((isOnline) => isOnline),
                mergeMap(() => this.checkSyncRequestQueue()),
            )
            .subscribe();
    }

    public getLastSyncTime(): moment.Moment {
        return this.lastSync;
    }

    public updateLastSyncTime(): void {
        this.lastSync = moment();
    }

    public getLastSyncBootStockTime(): moment.Moment {
        return this.lastSyncBootStock;
    }

    public async updateLastSyncBootStockTime(): Promise<void> {
        this.lastSyncBootStock = moment();
        return this.localStorageAppStateService.saveLastSyncBootStockTime(this.lastSyncBootStock);
    }

    public receiveNotifications(triggerSyncEvent = true): Observable<void> {
        this.log.debug(
            'SynchronizationService: receiveNotifications' + (triggerSyncEvent ? ' and trigger sync event' : ''),
        );
        return this.synchronize(SYNC_REQUEST_TYPE.REFRESH_NOTIFICATIONS, triggerSyncEvent);
    }

    public receiveWhatsNew(triggerSyncEvent = true): Observable<void> {
        this.log.debug('SynchronizationService: receiveWhatsNew' + (triggerSyncEvent ? ' and trigger sync event' : ''));
        return this.synchronize(SYNC_REQUEST_TYPE.REFRESH_WHATSNEW, triggerSyncEvent);
    }

    public receiveEverything(triggerSyncEvent = true): Observable<void> {
        this.log.debug(
            'SynchronizationService: receiveEverything' + (triggerSyncEvent ? ' and trigger sync event' : ''),
        );
        return this.synchronize(SYNC_REQUEST_TYPE.REFRESH_ALL, triggerSyncEvent);
    }

    public receiveEverythingCacheFirst(triggerSyncEvent = true): Observable<void> {
        this.log.debug(
            'SynchronizationService: receiveEverythingCacheFirst' + (triggerSyncEvent ? ' and trigger sync event' : ''),
        );
        return this.synchronize(SYNC_REQUEST_TYPE.REFRESH_ALL_CACHE_FIRST, triggerSyncEvent);
    }

    public receiveBootStock(): Observable<void> {
        return this.userMasterService.isPaceOnlineUser()
            ? of(undefined)
            : this.synchronize(SYNC_REQUEST_TYPE.REFRESH_BOOTSTOCK, false);
    }

    public sendOfflineData(): Observable<void> {
        this.log.debug('SynchronizationService: sendOfflineData and trigger sync event');
        return this.synchronize(SYNC_REQUEST_TYPE.FLUSH_ONLY, true);
    }

    public sendOfflineDataAndReceiveNotificationsAndBootStock(): Observable<void> {
        this.log.debug(
            'SynchronizationService: sendOfflineDataAndReceiveNotificationsAndBootStock and trigger sync event',
        );
        return this.synchronize(SYNC_REQUEST_TYPE.FLUSH_AND_REFRESH_NOTIFICATIONS_AND_BOOTSTOCK, true);
    }

    public resetOfflineStore(): void {
        this.log.debug('SynchronizationService: resetOfflineStore');
        return this.offlineService.resetOfflineStore();
    }

    /**
     * Generic synchronization function called by the above convenience functions.
     *
     * It accepts the sync request and executes it under the following conditions:
     *
     * 1. The current date and time seem valid and not manipulated
     * 2. There is currently no synchronization ongoing. If it is the sync request will be added to the queue.
     *
     * If checkSynchronizationPrerequisites() is falsy the catch block calls the error handler and mostly returns truthy, as
     * required tasks (e.g. re-authenticate) are performed by the error handler, and we are just waiting for a process to finish.
     *
     * Any real offline store error are caught by the catch block too. In this case the error from offline store is re-thrown to tell the callers
     * that something is wrong and an error message needs to be shown.
     *
     * @param syncRequestType
     * @param triggerSyncEvent
     */
    private synchronize(syncRequestType: SYNC_REQUEST_TYPE, triggerSyncEvent: boolean): Observable<void> {
        return from(this.dateManipulationCheckService.checkTimestamp(moment.utc())).pipe(
            // block sync in case date seems to be manipulated
            filter((timestampValid) => timestampValid),
            mergeMap(() => this.offlineService.isSyncing$),
            take(1),
            mergeMap((isSyncInProgress) => {
                if (isSyncInProgress) {
                    this.log.debug(`SynchronizationService: synchronize: sync in progress. Adding request to queue.`, {
                        syncRequestType,
                        triggerSyncEvent,
                    });
                    this.syncQueueService.addSyncRequest(syncRequestType, triggerSyncEvent);
                    return EMPTY;
                } else {
                    this.log.debug(`SynchronizationService: synchronize: synchronising now.`, {
                        syncRequestType,
                        triggerSyncEvent,
                    });
                }
                return this.connectivityService.isOnlineButWithErrors$.pipe(
                    take(1),
                    mergeMap(() => {
                        switch (syncRequestType) {
                            // REFRESH EVERYTHING
                            case SYNC_REQUEST_TYPE.REFRESH_ALL:
                            case SYNC_REQUEST_TYPE.REFRESH_ALL_CACHE_FIRST:
                                return this.offlineService
                                    .refreshAll(syncRequestType === SYNC_REQUEST_TYPE.REFRESH_ALL_CACHE_FIRST)
                                    .pipe(
                                        filter((isRefreshDone) => isRefreshDone),
                                        take(1),
                                        tap(() => {
                                            this.updateLastSyncBootStockTime().catch((error) => {
                                                this.log.error(
                                                    this.constructor.name,
                                                    'Error updating last sync boot stock time',
                                                    JSON.stringify(error),
                                                );
                                            });
                                            this.updateLastSyncTime();
                                            this.eventService.triggerBootStockSetSyncCompleted();
                                        }),
                                    );
                            // REFRESH NOTIFICATIONS ONLY
                            case SYNC_REQUEST_TYPE.REFRESH_NOTIFICATIONS:
                                return this.offlineService.refreshRequest(ENDPOINTS.NotificationHeaders).pipe(
                                    filter((isRefreshDone) => isRefreshDone),
                                    take(1),
                                    tap(() => {
                                        this.updateLastSyncTime();
                                        this.requestNotificationRefresh();
                                    }),
                                );

                            // REFRESH WHATSNEW ONLY
                            case SYNC_REQUEST_TYPE.REFRESH_WHATSNEW:
                                return this.offlineService.refreshRequest(ENDPOINTS.WhatsNew).pipe(
                                    filter((isRefreshDone) => isRefreshDone),
                                    take(1),
                                    tap(() => this.updateLastSyncTime()),
                                );
                            // REFRESH BOOT STOCK
                            case SYNC_REQUEST_TYPE.REFRESH_BOOTSTOCK:
                                return this.offlineService.refreshRequest(ENDPOINTS.MaterialStockSet).pipe(
                                    filter((isRefreshDone) => isRefreshDone),
                                    take(1),
                                    tap(() => {
                                        this.updateLastSyncBootStockTime().catch((error) => {
                                            this.log.error(
                                                this.constructor.name,
                                                'Error updating last sync boot stock time',
                                                JSON.stringify(error),
                                            );
                                        });
                                        this.updateLastSyncTime();
                                        this.eventService.triggerBootStockSetSyncCompleted();
                                    }),
                                );
                            // FLUSH
                            case SYNC_REQUEST_TYPE.FLUSH_ONLY:
                                return this.offlineService.sendOfflineData().pipe(
                                    take(1),
                                    tap(() => this.updateLastSyncTime()),
                                );
                            // FLUSH + REFRESH NOTIFICATIONS + REFRESH BOOT STOCK
                            case SYNC_REQUEST_TYPE.FLUSH_AND_REFRESH_NOTIFICATIONS_AND_BOOTSTOCK:
                                return this.offlineService.sendOfflineData().pipe(
                                    mergeMap(() =>
                                        this.offlineService.refreshRequest(ENDPOINTS.NotificationHeaders).pipe(
                                            filter(Boolean),
                                            take(1),
                                            mergeMap(() =>
                                                this.offlineService
                                                    .refreshRequest(ENDPOINTS.MaterialStockSet)
                                                    .pipe(filter(Boolean), take(1)),
                                            ),
                                            tap(() => {
                                                this.updateLastSyncBootStockTime().catch((error) => {
                                                    this.log.error(
                                                        this.constructor.name,
                                                        'Error updating last sync boot stock time',
                                                        JSON.stringify(error),
                                                    );
                                                });
                                                this.updateLastSyncTime();
                                                this.eventService.triggerBootStockSetSyncCompleted();
                                            }),
                                        ),
                                    ),
                                );
                            default:
                                return throwError(
                                    () =>
                                        new Error(
                                            `SynchronizationService: synchronize: Unknown sync request type '${syncRequestType}'`,
                                        ),
                                );
                        }
                    }),
                    tap(
                        () => this.triggerSyncEvent(syncRequestType, triggerSyncEvent),
                        () => this.triggerSyncEvent(syncRequestType, triggerSyncEvent),
                    ),
                    mergeMap(() => this.checkSyncRequestQueue()),
                    catchError((error: Error) => {
                        // Being here can have following reasons:
                        // - unknown error
                        // - no internet connection
                        // - no SAP connection
                        // - not authenticated
                        // - an offline plugin sync error
                        //
                        // For offline errors we dont call the handleSynchronizationPrerequisitesError as this would check the
                        // sync queue and try it again and end up in an endless loop. Therefore we just re-throw the error.
                        // Same for unknown errors - we simply do not know how to deal with them.
                        if (
                            !isConnectionErrorCode(error) ||
                            error === CONNECTION_ERROR_CODES.OFFLINE_PLUGIN_SYNC_ERROR
                        ) {
                            this.log.error(`SynchronizationService: synchronize: '${error?.message}'`);
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.WARN,
                                <string>this.translate.instant('APP_SYNC_FAILED_TITLE'),
                                <string>this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION'),
                            );
                            return throwError(error);
                        }

                        // If we can handle the error, let's do it :)
                        return this.handleSynchronizationPrerequisitesError(syncRequestType, error);
                    }),
                    mapTo(undefined),
                );
            }),
        );
    }

    /**
     * Handles error that deal with a missing internet connection or a not reachable Gateway or an unauthenticated user.
     * A missing internet connection will end gracefully with a resolve() which will tell the caller of synchronize()
     * that everything is fine so far, there is just no internet connection and no error message is show anywhere.
     *
     * Unauthenticated users have already triggered an authentication. Once the authentication finishes,
     * it will trigger the event "triggerAuthenticationFinished". The synchronization-service
     * will then check the request queue again. Therefore we just resolve() in this case to tell the caller that everything
     * is fine so far and no error should be shown at this time.
     *
     * @param syncRequestType
     * @param error
     */
    private handleSynchronizationPrerequisitesError(
        syncRequestType: SYNC_REQUEST_TYPE,
        error: CONNECTION_ERROR_CODES,
    ): Observable<void> {
        if (error === CONNECTION_ERROR_CODES.NO_INTERNET_CONNECTION) {
            this.log.debug(
                `SynchronizationService: handleSynchronizationPrerequisitesError: No internet connection detected. Adding request to queue.`,
            );
            this.syncQueueService.addSyncRequest(syncRequestType);
            return of(undefined);
        } else {
            this.log.error(`SynchronizationService: handleSynchronizationPrerequisitesError: ${error.toString()}`);
            return throwError(() => error);
        }
    }

    public flushCompletely(): Observable<boolean> {
        return this.offlineService.sendOfflineData();
    }

    /**
     * Asks for the notification list to be reloaded: a store dispatch once the notification domain
     * is served by the slice, and the `EventService` event the service behind
     * `NotificationDataInstanceToken` listens to while it is not.
     */
    private requestNotificationRefresh(): void {
        if (this.ngrxEnabled) {
            this.store.dispatch(notificationRefreshRequired());
            return;
        }
        this.eventService.triggerNotificationRefreshRequiredChanged(true);
    }

    private triggerSyncEvent(syncRequestType: SYNC_REQUEST_TYPE, triggerSyncEvent: boolean): void {
        if (this.connectivityService.isInternetConnectionAvailable() && triggerSyncEvent) {
            if (this.configService.getEnablePhysicalInventory()) {
                this.eventService.triggerPIAlertUpdate();
            }
            if (
                syncRequestType === SYNC_REQUEST_TYPE.REFRESH_ALL ||
                syncRequestType === SYNC_REQUEST_TYPE.REFRESH_NOTIFICATIONS ||
                syncRequestType === SYNC_REQUEST_TYPE.FLUSH_AND_REFRESH_NOTIFICATIONS_AND_BOOTSTOCK
            ) {
                this.log.debug(
                    'SynchronizationService: synchronize: refresh: triggering event "triggerSyncReceiveCompleted" and requesting a notification refresh',
                );
                this.eventService.triggerSyncReceiveCompleted();
                this.requestNotificationRefresh();
            }
            if (syncRequestType === SYNC_REQUEST_TYPE.REFRESH_WHATSNEW) {
                this.log.debug(
                    'SynchronizationService: synchronize: refreshWhatsNew: triggering event "triggerNewUpdatesRequired"',
                );
                this.eventService.triggerNewUpdatesRequired();
            }
        }
    }

    private checkSyncRequestQueue(): Observable<void> {
        let nextSyncRequest: SyncRequestModel;
        return this.syncQueueService.syncRequest$.pipe(
            take(1),
            mergeMap((syncRequests) => {
                if (syncRequests.length > 0) {
                    nextSyncRequest = syncRequests[0];
                    this.log.debug(
                        `SynchronizationService: checkSyncRequestQueue: ${nextSyncRequest.toString()} will be processed. Total ${
                            syncRequests.length
                        } sync requests`,
                    );
                    // Remove the request from sync queue BEFORE calling synchronize
                    // as synchronize will again call this method and would end up in infinite loop
                    this.syncQueueService.removeSynchronizationRequest(nextSyncRequest);
                    return this.synchronize(nextSyncRequest.syncRequestType, nextSyncRequest.triggerSyncEvent);
                } else {
                    return of(undefined);
                }
            }),
            catchError((error: Error) => {
                this.log.error(
                    `SynchronizationService: checkSyncRequestQueue: failed for ${nextSyncRequest.toString()} with error: ${JSON.stringify(
                        error?.message,
                    )}`,
                );
                this.syncQueueService.addSyncRequest(nextSyncRequest.syncRequestType, nextSyncRequest.triggerSyncEvent);
                return of(undefined);
            }),
        );
    }
}
